'use client';

import React, { useCallback } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faSliders, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { Drawer, Tab, TabPanel, Tabs, TabSet } from 'src/components';
import { IconButton } from 'src/components/PulseUI/IconButton';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { ManageFilters } from './components';

type ManageFiltersDrawerProps = {
  hideBackdrop?: boolean;
  anchor?: 'right' | 'left' | 'top' | 'bottom';
  size?: 'lg' | 'sm' | 'md' | 'xl' | '2xl' | '3xl' | 'base' | 'base2';
};

const Sections = ['Personal', 'Company'] as const;

const ManageFiltersDrawer: React.FC<ManageFiltersDrawerProps> = ({
  hideBackdrop = false,
  anchor = 'right',
  size = 'base2',
}) => {
  const { isDrawerOpen, closeAllButCurrent } = useDrawer();

  const handleCloseDrawer = useCallback(() => {
    closeAllButCurrent('resultsDrawer');
  }, [closeAllButCurrent]);

  return (
    <div data-testid="manage-filters-drawer">
      <Drawer
        isOpen={isDrawerOpen('manageFiltersDrawer')}
        onClose={handleCloseDrawer}
        hideBackdrop={hideBackdrop}
        anchor={anchor}
        size={size}
        data-hide-backdrop={hideBackdrop}
        data-anchor={anchor}
        data-size={size}
        variant="temporary"
      >
        <Drawer.Header className="bg-sky-100 text-primary border-b-0">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <Icon
                icon={faSliders}
                className="text-primary text-md"
                aria-hidden="true"
              />
              <div className="font-semibold">Saved Filters</div>
            </div>
            <div className="flex items-center gap-2">
              <IconButton
                variant="plain"
                color="neutral"
                size="sm"
                onClick={handleCloseDrawer}
                icon={faXmark}
                className="text-primary hover:bg-sky-200"
                aria-label="Close filters drawer"
                data-testid="close-filters-drawer-button"
              />
            </div>
          </div>
        </Drawer.Header>
        <Drawer.Content className="flex flex-col h-full">
          <Tabs className="flex flex-col h-full">
            <TabSet
              id="manage-filters-drawer-tabs"
              className="flex-none"
              data-testid="manage-filters-tabset"
            >
              {Sections.map((section, index) => (
                <Tab
                  key={section}
                  tabSetId="manage-filters-drawer-tabs"
                  index={index}
                  className="justify-center w-full whitespace-nowrap text-sm flex items-center gap-2"
                  data-testid={`manage-filters-tab-${section.toLowerCase()}`}
                >
                  {section}
                </Tab>
              ))}
            </TabSet>
            <div
              className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
              data-testid="manage-filters-content"
            >
              {Sections.map((section, index) => (
                <TabPanel
                  key={section}
                  tabSetId="manage-filters-drawer-tabs"
                  index={index}
                  className="h-full"
                  data-testid={`manage-filters-panel-${section.toLowerCase()}`}
                >
                  <ManageFilters section={section} />
                </TabPanel>
              ))}
            </div>
          </Tabs>
        </Drawer.Content>
      </Drawer>
    </div>
  );
};

export default ManageFiltersDrawer;
